import { cn } from "@/lib/utils";
import { useSidebar } from "@/components/ui/sidebar";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Users, Pencil } from "lucide-react";
import type { TourAudienceRecord } from "./types";

interface AudienceNavProps {
  audiences: TourAudienceRecord[];
  isLoading: boolean;
  selectedId: string | null;
  onSelect: (id: string) => void;
  onCreate: () => void;
  onEdit: (a: TourAudienceRecord) => void;
}

export function AudienceNav({
  audiences,
  isLoading,
  selectedId,
  onSelect,
  onCreate,
  onEdit,
}: AudienceNavProps) {
  const { collapsed, isMobile } = useSidebar();
  const isCollapsed = collapsed && !isMobile;

  if (isLoading) {
    return (
      <div
        className={cn(
          "shrink-0 border-r bg-background p-3 space-y-2 transition-all duration-200 hidden md:block",
          isCollapsed ? "w-16" : "w-60",
        )}
      >
        {!isCollapsed && <Skeleton className="h-6 w-24 mb-4" />}
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className={cn("h-9", isCollapsed ? "w-10" : "w-full")} />
        ))}
      </div>
    );
  }

  return (
    <nav
      className={cn(
        "shrink-0 border-r bg-background hidden md:flex md:flex-col transition-all duration-200",
        isCollapsed ? "w-16" : "w-60",
      )}
      aria-label="Tour audience navigation"
      data-testid="nav-tour-audiences"
    >
      {!isCollapsed && (
        <div className="px-4 pt-8 pb-3 border-b flex items-center justify-between gap-2">
          <h2 className="text-lg font-semibold">Audiences</h2>
          <Button
            size="icon"
            variant="ghost"
            onClick={onCreate}
            data-testid="button-add-audience"
            aria-label="Add audience"
          >
            <Plus className="w-4 h-4" />
          </Button>
        </div>
      )}
      <div className="flex-1 overflow-y-auto p-2 space-y-1">
        {audiences.map((a) => {
          const active = a.id === selectedId;
          return (
            <div
              key={a.id}
              className={cn(
                "group flex items-center gap-2 rounded-md px-2 py-2 hover-elevate active-elevate-2 cursor-pointer",
                active && "bg-sidebar-accent",
              )}
              onClick={() => onSelect(a.id)}
              data-testid={`row-audience-${a.slug}`}
            >
              <Users className="w-4 h-4 shrink-0 text-muted-foreground" />
              {!isCollapsed && (
                <>
                  <span className="flex-1 truncate text-sm">{a.label}</span>
                  <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    className="opacity-0 group-hover:opacity-100"
                    onClick={(e) => {
                      e.stopPropagation();
                      onEdit(a);
                    }}
                    aria-label={`Edit ${a.label}`}
                    data-testid={`button-edit-audience-${a.slug}`}
                  >
                    <Pencil className="w-3 h-3" />
                  </Button>
                </>
              )}
            </div>
          );
        })}
        {audiences.length === 0 && !isCollapsed && (
          <p className="text-xs text-muted-foreground p-2">
            No audiences yet. Click + to create one.
          </p>
        )}
      </div>
    </nav>
  );
}

export function AudienceNavMobile({
  audiences,
  isLoading,
  selectedId,
  onSelect,
  onCreate,
  onEdit,
}: Pick<
  AudienceNavProps,
  "audiences" | "isLoading" | "selectedId" | "onSelect" | "onCreate" | "onEdit"
>) {
  if (isLoading) {
    return <Skeleton className="h-9 w-full md:hidden mb-4" />;
  }
  const selected = audiences.find((a) => a.id === selectedId) ?? null;
  return (
    <div className="md:hidden mb-4 flex items-center gap-2">
      <div className="flex-1">
        <Select value={selectedId ?? ""} onValueChange={onSelect}>
          <SelectTrigger data-testid="select-audience-mobile">
            <SelectValue placeholder="Select audience" />
          </SelectTrigger>
          <SelectContent>
            {audiences.map((a) => (
              <SelectItem key={a.id} value={a.id}>
                {a.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      {selected && (
        <Button
          size="icon"
          variant="ghost"
          onClick={() => onEdit(selected)}
          aria-label={`Edit ${selected.label}`}
          data-testid="button-edit-audience-mobile"
        >
          <Pencil className="w-4 h-4" />
        </Button>
      )}
      <Button
        size="icon"
        variant="default"
        onClick={onCreate}
        aria-label="Add audience"
        data-testid="button-add-audience-mobile"
      >
        <Plus className="w-4 h-4" />
      </Button>
    </div>
  );
}
